import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  LogOut, Search, RefreshCw, Trash2, Phone, Mail, MapPin, Calendar, Users, Sparkles,
  Loader2, Inbox, TrendingUp, Handshake, CheckCircle2, XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { BRAND } from '../data/mock';
import AdminTabs from '../components/AdminTabs';

const STATUS_META = {
  new:        { label: 'New',        cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  contacted:  { label: 'Contacted',  cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  quoted:     { label: 'Quoted',     cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  won:        { label: 'Won',        cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  lost:       { label: 'Lost',       cls: 'bg-stone-200 text-stone-600 border-stone-300' },
};

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white rounded-2xl border border-pink-100 shadow-sm p-4 flex items-center gap-3" data-testid={`admin-stat-${label.toLowerCase()}`}>
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-2xl font-display font-bold text-gray-900 leading-none">{value}</div>
      <div className="text-xs text-stone-500 mt-1">{label}</div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const { user, loading, logout, authAxios } = useAuth();
  const navigate = useNavigate();

  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, quoted: 0, won: 0, lost: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: list }, { data: st }] = await Promise.all([
        authAxios.get('/admin/enquiries', { params: { status: statusFilter, q: q || undefined } }),
        authAxios.get('/admin/enquiries/stats'),
      ]);
      setEnquiries(list);
      setStats(st);
    } finally {
      setBusy(false);
    }
  }, [authAxios, statusFilter, q]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const selected = enquiries.find((e) => e.id === selectedId) || null;

  const updateEnquiry = async (id, patch) => {
    const { data } = await authAxios.patch(`/admin/enquiries/${id}`, patch);
    setEnquiries((list) => list.map((e) => (e.id === id ? data : e)));
    if (selected?.id === id) setNotesDraft(data.admin_notes || '');
    // refresh stats
    const { data: st } = await authAxios.get('/admin/enquiries/stats');
    setStats(st);
  };

  const removeEnquiry = async (id) => {
    if (!window.confirm('Delete this enquiry permanently?')) return;
    await authAxios.delete(`/admin/enquiries/${id}`);
    setEnquiries((list) => list.filter((e) => e.id !== id));
    if (selected?.id === id) setSelectedId(null);
    const { data: st } = await authAxios.get('/admin/enquiries/stats');
    setStats(st);
  };

  const openDetail = (e) => {
    setSelectedId(e.id);
    setNotesDraft(e.admin_notes || '');
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Top bar */}
      <header className="bg-white border-b border-pink-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="logo" className="w-10 h-10 rounded-full object-contain" />
            <div className="leading-tight">
              <div className="font-script text-xs text-pink-600 -mb-0.5">Simply Original</div>
              <div className="font-display font-bold text-stone-900">Mr. Coconetto · Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-stone-500">{user.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-full border-pink-300 text-pink-700 hover:bg-pink-50"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <AdminTabs />
        </div>
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <StatCard icon={Inbox} label="Total" value={stats.total} tint="bg-blush text-pink-brand" />
          <StatCard icon={Sparkles} label="New" value={stats.new} tint="bg-blue-100 text-blue-700" />
          <StatCard icon={TrendingUp} label="Contacted" value={stats.contacted} tint="bg-amber-100 text-amber-700" />
          <StatCard icon={Handshake} label="Quoted" value={stats.quoted} tint="bg-purple-100 text-purple-700" />
          <StatCard icon={CheckCircle2} label="Won" value={stats.won} tint="bg-emerald-100 text-emerald-700" />
          <StatCard icon={XCircle} label="Lost" value={stats.lost} tint="bg-gray-200 text-gray-600" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-pink-100 shadow-sm p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="Search name, phone, email, city…"
              className="pl-9 rounded-full"
              data-testid="admin-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] rounded-full" data-testid="admin-status-filter-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="admin-status-filter-all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <SelectItem key={k} value={k} data-testid={`admin-status-filter-${k}`}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={load}
            variant="outline"
            className="rounded-full"
            disabled={busy}
            data-testid="admin-refresh-button"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {/* Grid: list + detail */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-2 space-y-3" data-testid="admin-enquiries-list">
            {busy && enquiries.length === 0 ? (
              <div className="p-10 text-center text-stone-500 bg-white rounded-2xl border border-pink-100">
                <Loader2 className="w-6 h-6 mx-auto animate-spin text-pink-brand" />
                <div className="mt-2 text-sm">Loading enquiries…</div>
              </div>
            ) : enquiries.length === 0 ? (
              <div className="p-14 text-center bg-white rounded-2xl border border-dashed border-pink-200" data-testid="admin-empty-state">
                <div className="w-16 h-16 rounded-full bg-blush/60 flex items-center justify-center mx-auto mb-3">
                  <Inbox className="w-7 h-7 text-pink-brand" />
                </div>
                <div className="font-display font-semibold text-gray-800">No enquiries yet</div>
                <div className="text-sm text-stone-500 mt-1">
                  When someone fills the Request-a-Quote form, it'll show up here in real time.
                </div>
              </div>
            ) : (
              enquiries.map((e) => {
                const meta = STATUS_META[e.status] || STATUS_META.new;
                const active = e.id === selectedId;
                return (
                  <button
                    type="button"
                    key={e.id}
                    onClick={() => openDetail(e)}
                    className={`w-full text-left bg-white rounded-2xl border p-4 transition-all shadow-sm hover:shadow-md ${
                      active ? 'border-pink-300 ring-2 ring-pink-100' : 'border-pink-100'
                    }`}
                    data-testid={`admin-enquiry-row-${e.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display font-bold text-gray-900">{e.name}</span>
                          <Badge className={`${meta.cls} border rounded-full text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5`}>
                            {meta.label}
                          </Badge>
                          {e.plan_interest && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-pink-700 bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5">
                              {e.plan_interest}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 mt-1 flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-pink-500" /> {e.event_type}</span>
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-pink-brand" /> {e.city}</span>
                          {e.event_date && (
                            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-stone-500" /> {e.event_date}</span>
                          )}
                          {e.guest_count && (
                            <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5 text-stone-500" /> {e.guest_count}</span>
                          )}
                        </div>
                        <div className="text-xs text-stone-500 mt-1 flex items-center gap-3">
                          <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {e.phone}</span>
                          {e.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {e.email}</span>}
                        </div>
                      </div>
                      <div className="text-[11px] text-gray-400 whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Detail panel */}
          <aside className="lg:sticky lg:top-24 h-fit" data-testid="admin-detail-panel">
            {!selected ? (
              <div className="bg-white rounded-2xl border border-dashed border-pink-200 p-8 text-center text-sm text-stone-500">
                Select an enquiry to view details and update status.
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-pink-100 shadow-sm p-5 space-y-4">
                <div>
                  <div className="text-xs text-stone-500">Enquiry from</div>
                  <div className="font-display font-bold text-xl text-gray-900">{selected.name}</div>
                  <div className="text-xs text-stone-500 mt-0.5">Received {new Date(selected.created_at).toLocaleString('en-IN')}</div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <a href={`tel:${selected.phone}`} className="rounded-xl border border-pink-200 bg-blush/60 px-3 py-2 text-stone-900 hover:bg-blush inline-flex items-center gap-2" data-testid="admin-detail-call-link">
                    <Phone className="w-3.5 h-3.5" /> Call
                  </a>
                  <a href={`https://wa.me/${selected.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="rounded-xl border border-pink-200 bg-pink-50 px-3 py-2 text-pink-700 hover:bg-pink-100 inline-flex items-center gap-2" data-testid="admin-detail-whatsapp-link">
                    <Sparkles className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </div>

                <div className="grid gap-1.5 text-sm text-gray-700">
                  <div><span className="text-stone-500">Phone:</span> {selected.phone}</div>
                  {selected.email && <div><span className="text-stone-500">Email:</span> {selected.email}</div>}
                  <div><span className="text-stone-500">Event:</span> {selected.event_type}</div>
                  <div><span className="text-stone-500">City:</span> {selected.city}</div>
                  {selected.event_date && <div><span className="text-stone-500">Date:</span> {selected.event_date}</div>}
                  {selected.guest_count != null && <div><span className="text-stone-500">Guests:</span> {selected.guest_count}</div>}
                  {selected.plan_interest && <div><span className="text-stone-500">Plan:</span> {selected.plan_interest}</div>}
                  {selected.message && (
                    <div className="mt-1 rounded-lg bg-gray-50 border border-gray-200 p-2 text-gray-700 whitespace-pre-wrap">
                      {selected.message}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Status</div>
                  <Select
                    value={selected.status}
                    onValueChange={(v) => updateEnquiry(selected.id, { status: v })}
                  >
                    <SelectTrigger data-testid="admin-detail-status-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_META).map(([k, m]) => (
                        <SelectItem key={k} value={k} data-testid={`admin-detail-status-option-${k}`}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Internal Notes</div>
                  <Textarea
                    rows={3}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="Follow-up notes, quote amount, next steps…"
                    data-testid="admin-detail-notes-input"
                  />
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => updateEnquiry(selected.id, { admin_notes: notesDraft })}
                      className="rounded-full bg-pink-brand hover:bg-[#BE185D] text-white"
                      data-testid="admin-detail-save-notes-button"
                    >
                      Save notes
                    </Button>
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                  <span className="text-[11px] text-gray-400">ID {selected.id.slice(0, 8)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeEnquiry(selected.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                    data-testid="admin-detail-delete-button"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
